import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdVerified, MdNearMe } from 'react-icons/md';
import { api } from '../services/api';
import usePoll from '../hooks/usePoll';
import { useLocation } from '../context/LocationContext';
import LocationChip from '../components/LocationChip';
import {
  NEARBY_MAX_KM,
  matchesHospitalFilter,
  hospitalLatLng,
} from '../utils/hospitalFilters';

function ls(h) {
  if (h.isShutdown) return { label: '🔴 Shutdown', cls: 'badge-red' };
  if (h.is24Hours || h.is24x7) return { label: '🟢 24/7', cls: 'badge-green' };
  const d = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = d[new Date().getDay()];
  const op = h.opDays || d.slice(1, 7);
  if (!op.includes(today)) return { label: '🌙 Closed', cls: 'badge-amber' };
  const now = new Date();
  const nm = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = (h.opStart || '08:00').split(':').map(Number);
  const [eh, em] = (h.opEnd || '18:00').split(':').map(Number);
  return nm >= sh * 60 + sm && nm < eh * 60 + em
    ? { label: '🟢 Open', cls: 'badge-green' }
    : { label: '🟡 Closed', cls: 'badge-amber' };
}

const FILTER_CHIPS = [
  { id: 'all',        label: 'All' },
  { id: 'government', label: 'Government' },
  { id: 'private',    label: 'Private' },
  { id: 'trust',      label: 'Trust' },
  { id: 'ntr',        label: '🩺 NTR Vaidya Seva' },
];

export default function HospitalListPage() {
  const navigate = useNavigate();
  const { coords, city, distanceTo, sortByDistance, source } = useLocation();
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [nearOnly, setNearOnly] = useState(false);

  usePoll(useCallback(async () => {
    const r = await api.getHospitals();
    setHospitals(r.data || []);
    setLoading(false);
  }, []), 2500);

  let filtered = hospitals.filter(h => {
    const q = search.toLowerCase().trim();
    const textOk = !q
      || h.name?.toLowerCase().includes(q)
      || h.city?.toLowerCase().includes(q)
      || (h.address || '').toLowerCase().includes(q);
    return textOk && matchesHospitalFilter(h, filter);
  });

  if (nearOnly) {
    if (coords) {
      filtered = filtered.filter(h => {
        const ll = hospitalLatLng(h);
        if (!ll) return false;
        const d = distanceTo(ll.lat, ll.lng);
        return d != null && Number.isFinite(d) && d <= NEARBY_MAX_KM;
      });
    } else {
      filtered = [];
    }
  }

  if (coords) filtered = sortByDistance(filtered);

  const locHint = source === 'ip'
    ? ' · approx. location — tap 📍 for GPS'
    : source === 'manual'
      ? ' · city selected'
      : source === 'gps'
        ? ' · GPS'
        : '';

  return (
    <div className="fade-in">
      <div className="page">
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12, gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div className="section-title" style={{ margin: 0 }}>🏥 All Hospitals</div>
            <div className="section-sub">
              {hospitals.length} hospitals on HCEN{city ? ` · ${city}` : ''}{locHint}
            </div>
          </div>
          <LocationChip compact />
        </div>

        <div className="search-bar" style={{ marginBottom: 12, maxWidth: '100%' }}>
          <MdSearch />
          <input
            placeholder="Search hospitals or cities…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{
          display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
        }}>
          {FILTER_CHIPS.map(f => (
            <button
              key={f.id}
              className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
          <button
            className={`btn btn-sm ${nearOnly ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setNearOnly(v => !v)}
            title={`Show hospitals within ${NEARBY_MAX_KM} km`}
          >
            <MdNearMe style={{ verticalAlign: -2, marginRight: 4 }} />
            Near me
          </button>
        </div>

        {loading ? <div className="spinner" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(h => {
              const id = h.id || h.hospitalId;
              const st = ls(h);
              const ll = hospitalLatLng(h);
              const km = coords && ll ? distanceTo(ll.lat, ll.lng) : null;
              return (
                <div key={id} className="hospital-card" onClick={() => navigate(`/hospitals/${id}`)}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    {(() => {
                      const img = h.photoUrl || h.logoUrl
                        || (Array.isArray(h.imageUrls) && h.imageUrls[0]);
                      return img
                        ? (
                          <img
                            src={img}
                            alt={h.name}
                            className="hospital-img"
                            style={{ objectFit: 'cover', background: '#0f172a' }}
                            onError={e => {
                              e.target.replaceWith(Object.assign(
                                document.createElement('div'),
                                { className: 'hospital-img', style: 'font-size:26px', textContent: '🏥' },
                              ));
                            }}
                          />
                        )
                        : <div className="hospital-img" style={{ fontSize: 26 }}>🏥</div>;
                    })()}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span className="hospital-name">{h.name}</span>
                        {h.isTrusted && <MdVerified style={{ color: '#f59e0b' }} />}
                      </div>
                      <div className="hospital-addr">📍 {h.address || h.city || '—'}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <span className={`badge ${st.cls}`}>{st.label}</span>
                        {km != null && Number.isFinite(km) && (
                          <span className="badge" style={{ background: '#eef2ff', color: '#4f46e5', fontWeight: 700 }}>
                            📍 {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`}
                          </span>
                        )}
                        {h.opStart && h.opEnd && (
                          <span className="badge badge-blue">⏰ {h.opStart}–{h.opEnd}</span>
                        )}
                        {Number(h.rating) > 0 && (
                          <span className="badge badge-primary">
                            ⭐ {Number(h.rating).toFixed(1)}
                          </span>
                        )}
                        {h.hasNtrVaidyaSeva && (
                          <span className="badge badge-green">🩺 NTR Vaidya Seva</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 20, color: 'var(--text-muted)' }}>›</div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="empty">
                <div className="empty-icon">🔍</div>
                <div className="empty-title">No hospitals found</div>
                {nearOnly && !coords && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                    Tap the location chip above and choose “Use my current location” for Near me.
                  </div>
                )}
                {nearOnly && coords && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                    No hospitals within {NEARBY_MAX_KM} km. Turn off “Near me” to see all.
                  </div>
                )}
                {filter === 'trust' && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                    Trust hospitals are marked with a verified badge or Government type.
                  </div>
                )}
                {filter === 'ntr' && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                    Hospitals accepting NTR Vaidya Seva will appear here once enabled by admin.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
